/**
 * Reservation capacity helpers: free tables for a slot, total capacity, tables that fit party size,
 * and event-scoped slot guest aggregation for buffet / multi-day events.
 */

import type { Table, Reservation, Event, ReservationStatus } from '../types';

/** Reservations that count toward per-slot guest limits (not waitlist). */
export const SLOT_GUEST_COUNT_STATUSES: ReservationStatus[] = ['pending', 'confirmed', 'seated'];

/** Statuses that still hold a table for a date/time slot (capacity / assign). */
export const TABLE_HOLD_STATUSES: ReservationStatus[] = ['pending', 'confirmed', 'seated'];

/** True if this reservation is assigned to the table (primary or merged assign list). */
export function reservationHoldsTable(r: Reservation, tableId: number): boolean {
  if (Number(r.tableId) === Number(tableId)) return true;
  if (r.assignedTableIds?.some((id) => Number(id) === Number(tableId))) return true;
  return false;
}

/**
 * Today's seated reservation on this table — keeps floor `occupied` without a POS order
 * (Option A: Arrived does not auto-create an order).
 */
export function tableHasSeatedReservation(
  reservations: Reservation[],
  tableId: number,
  todayIso: string,
  excludeReservationId?: number,
): boolean {
  return reservations.some(
    (r) =>
      r.id !== excludeReservationId &&
      r.reservationDate === todayIso &&
      r.status === 'seated' &&
      reservationHoldsTable(r, tableId),
  );
}

/**
 * Seated bookings on `sourceTableId` for today that must move when a dine-in/buffet order transfers.
 * Without this, the source table stays floor-red (`seated`) after the order leaves.
 */
export function seatedReservationsToMoveOnOrderTransfer(
  reservations: Reservation[],
  sourceTableId: number,
  todayIsoDates: string | string[],
): Reservation[] {
  const dates = new Set(
    (Array.isArray(todayIsoDates) ? todayIsoDates : [todayIsoDates]).filter(Boolean),
  );
  return reservations.filter(
    (r) =>
      r.status === 'seated' &&
      dates.has(r.reservationDate) &&
      reservationHoldsTable(r, sourceTableId),
  );
}

/**
 * Reassign a seated reservation onto the transfer target as a single-table hold.
 * Clears join/merge assignment so sibling merge tables are not left occupied.
 */
export function buildReservationUpdatesForOrderTransfer(
  reservation: Reservation,
  sourceTableId: number,
  targetTableId: number,
): Partial<Reservation> | null {
  return buildReservationUpdatesForTableTransfer(reservation, sourceTableId, targetTableId, {
    statuses: ['seated'],
  });
}

/** Statuses that can be moved with Transfer table before (or without) an order. */
export const RESERVATION_TABLE_TRANSFER_STATUSES: ReservationStatus[] = [
  'pending',
  'confirmed',
  'seated',
];

/**
 * Reassign a reservation onto a new table (pending / confirmed / seated).
 * Default: collapses join holds to the single target so old tables free correctly.
 * Pass `assignedTableIds` (2+) to move onto a new join set instead (primary = first id).
 */
export function buildReservationUpdatesForTableTransfer(
  reservation: Reservation,
  sourceTableId: number,
  targetTableId: number,
  options?: { statuses?: ReservationStatus[]; assignedTableIds?: number[] },
): Partial<Reservation> | null {
  const allowed = options?.statuses ?? RESERVATION_TABLE_TRANSFER_STATUSES;
  if (!allowed.includes(reservation.status)) return null;
  if (!reservationHoldsTable(reservation, sourceTableId)) return null;
  const source = Number(sourceTableId);
  const target = Number(targetTableId);
  if (!Number.isFinite(source) || !Number.isFinite(target)) return null;

  const joinIds = (options?.assignedTableIds ?? [])
    .map(Number)
    .filter((n) => Number.isFinite(n) && n > 0);
  const uniqueJoin = [...new Set(joinIds)];

  if (uniqueJoin.length >= 2) {
    // Primary is first selected; must include targetTableId as primary for callers that pass both.
    const primary = uniqueJoin.includes(target) ? target : uniqueJoin[0];
    const ordered = [primary, ...uniqueJoin.filter((id) => id !== primary)];
    // Allow "same primary" when the join set itself changed.
    const prev = (reservation.assignedTableIds ?? []).map(Number).filter((n) => Number.isFinite(n) && n > 0);
    const prevSet = new Set(prev.length ? prev : reservation.tableId ? [Number(reservation.tableId)] : []);
    const sameSet =
      ordered.length === prevSet.size && ordered.every((id) => prevSet.has(id));
    if (sameSet) return null;

    return {
      tableId: primary,
      assignedTableIds: ordered,
      requiresMerge: true,
    };
  }

  if (source === target) return null;

  const updates: Partial<Reservation> = {
    tableId: target,
    requiresMerge: false,
  };
  if (reservation.assignedTableIds && reservation.assignedTableIds.length > 0) {
    updates.assignedTableIds = [target];
  }
  return updates;
}

/** Minutes before reservation time when the floor paints amber reserved. */
export const FLOOR_RESERVATION_LEAD_MINUTES = 60;
/** Minutes after reservation time amber stays if guest is delayed (then clears if still pending/confirmed). */
export const FLOOR_RESERVATION_GRACE_MINUTES = 15;

/**
 * True when a pending/confirmed reservation should paint the floor amber:
 * from (time − lead) through (time + grace). Not all day.
 */
export function isReservationSoonForFloor(
  r: Reservation,
  now: Date = new Date(),
  options?: { leadMinutes?: number; graceMinutes?: number },
): boolean {
  if (r.status !== 'pending' && r.status !== 'confirmed') return false;
  const todayIso = now.toISOString().split('T')[0];
  if (r.reservationDate !== todayIso) return false;
  const [h, m] = r.reservationTime.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return true;
  const lead = options?.leadMinutes ?? FLOOR_RESERVATION_LEAD_MINUTES;
  const grace = options?.graceMinutes ?? FLOOR_RESERVATION_GRACE_MINUTES;
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const resMinutes = h * 60 + m;
  return nowMinutes >= resMinutes - lead && nowMinutes <= resMinutes + grace;
}

/** True when this is a general (non-event) reservation. */
export function isGeneralReservation(r: Reservation): boolean {
  return r.eventId == null || r.eventId === '';
}

export function isBuffetMultiSlotEvent(event: Event): boolean {
  if (event.bookingFormMode === 'standard') return false;
  if (event.bookingFormMode === 'buffet_multi_slot') return true;
  if ((event.mealSlots?.length ?? 0) > 0) return true;
  if ((event.eventDates?.length ?? 0) > 1) return true;
  return false;
}

export function getEventBookingDates(event: Event): string[] {
  const extra = event.eventDates?.filter(Boolean) ?? [];
  if (extra.length > 0) return [...new Set(extra)].sort();
  return [event.eventDate];
}

/**
 * Next event to pre-select on the reservations page: earliest by start date among
 * events still running (last day ≥ today) or, if none, earliest event that starts on or after today.
 */
export function getNextEventIdForDefaultFilter(events: Event[], todayIso: string): 'all' | string {
  if (!events.length) return 'all';
  type Row = { id: string; first: string; last: string };
  const rows: Row[] = events
    .map((ev) => {
      const sorted = [...getEventBookingDates(ev)].filter(Boolean).sort();
      if (!sorted.length) return null;
      return { id: String(ev.id), first: sorted[0], last: sorted[sorted.length - 1] };
    })
    .filter((r): r is Row => r != null);

  const ongoingOrUpcoming = rows
    .filter((r) => r.last >= todayIso)
    .sort((a, b) => a.first.localeCompare(b.first) || a.id.localeCompare(b.id));
  if (ongoingOrUpcoming.length) return ongoingOrUpcoming[0].id;

  const futureStart = rows
    .filter((r) => r.first >= todayIso)
    .sort((a, b) => a.first.localeCompare(b.first) || a.id.localeCompare(b.id));
  if (futureStart.length) return futureStart[0].id;

  return 'all';
}

/** First event date on or after `todayIso`, else the event’s earliest date. */
export function firstEventDateOnOrAfter(event: Event, todayIso: string): string | undefined {
  const sorted = [...getEventBookingDates(event)].filter(Boolean).sort();
  if (!sorted.length) return undefined;
  return sorted.find((d) => d >= todayIso) ?? sorted[0];
}

export function eventAppliesToReservationDate(event: Event, reservationDate: string): boolean {
  return getEventBookingDates(event).includes(reservationDate);
}

export function getReservationSlotLabel(r: Reservation, event?: Event | null): string | undefined {
  if (!event?.mealSlots?.length || !r.slotId) return undefined;
  return event.mealSlots.find((s) => s.id === r.slotId)?.label;
}

function matchesEventSlotSlice(
  r: Reservation,
  eventId: string | number,
  reservationDate: string,
  reservationTime: string,
  slotId?: string
): boolean {
  if (String(r.eventId ?? '') !== String(eventId)) return false;
  if (r.reservationDate !== reservationDate) return false;
  if (r.reservationTime !== reservationTime) return false;
  if (slotId) {
    if (r.slotId && r.slotId !== slotId) return false;
  }
  return true;
}

function reservationAdultsForAggregate(r: Reservation): number {
  if (typeof r.adultsCount === 'number') return r.adultsCount;
  const kb = r.kidsBelow10Count ?? 0;
  const ka = r.kidsAbove10Count ?? 0;
  if (kb > 0 || ka > 0) return Math.max(0, r.partySize - kb - ka);
  if (typeof r.kidsCount === 'number') return Math.max(0, r.partySize - r.kidsCount);
  return r.partySize;
}

function reservationKidsBelow10ForAggregate(r: Reservation): number {
  if (typeof r.kidsBelow10Count === 'number') return r.kidsBelow10Count;
  if (typeof r.kidsCount === 'number') return r.kidsCount;
  return 0;
}

function reservationKids10PlusForAggregate(r: Reservation): number {
  return typeof r.kidsAbove10Count === 'number' ? r.kidsAbove10Count : 0;
}

/** Total children (under-10 + 10+ bands, or legacy kidsCount). */
function reservationKidsTotalForAggregate(r: Reservation): number {
  const kb = reservationKidsBelow10ForAggregate(r);
  const ka = reservationKids10PlusForAggregate(r);
  if (kb > 0 || ka > 0) return kb + ka;
  if (typeof r.kidsCount === 'number') return r.kidsCount;
  return 0;
}

/** Sum party size and guest breakdown for a list (e.g. staff dashboard filtered rows). */
export function summarizeReservationGuestTotals(reservations: Reservation[]): {
  bookingCount: number;
  partySize: number;
  adults: number;
  kids: number;
} {
  let partySize = 0;
  let adults = 0;
  let kids = 0;
  for (const r of reservations) {
    partySize += r.partySize;
    adults += reservationAdultsForAggregate(r);
    kids += reservationKidsTotalForAggregate(r);
  }
  return {
    bookingCount: reservations.length,
    partySize,
    adults,
    kids,
  };
}

export interface EventSlotGuestTotals {
  partyBooked: number;
  adultsBooked: number;
  kidsBelow10Booked: number;
  kids10PlusBooked: number;
}

/**
 * Sum party and guest breakdown for one event + date + time (and optional slot id).
 * Only reservations in `statuses` are included (default: pending, confirmed, seated).
 */
export function aggregateEventSlotGuests(
  reservations: Reservation[],
  eventId: string | number,
  reservationDate: string,
  reservationTime: string,
  options?: {
    slotId?: string;
    excludeReservationId?: number;
    statuses?: ReservationStatus[];
  }
): EventSlotGuestTotals {
  const statuses = options?.statuses ?? SLOT_GUEST_COUNT_STATUSES;
  let partyBooked = 0;
  let adultsBooked = 0;
  let kidsBelow10Booked = 0;
  let kids10PlusBooked = 0;
  for (const r of reservations) {
    if (options?.excludeReservationId != null && r.id === options.excludeReservationId) continue;
    if (!statuses.includes(r.status)) continue;
    if (!matchesEventSlotSlice(r, eventId, reservationDate, reservationTime, options?.slotId))
      continue;
    partyBooked += r.partySize;
    adultsBooked += reservationAdultsForAggregate(r);
    kidsBelow10Booked += reservationKidsBelow10ForAggregate(r);
    kids10PlusBooked += reservationKids10PlusForAggregate(r);
  }
  return { partyBooked, adultsBooked, kidsBelow10Booked, kids10PlusBooked };
}

export function getEffectiveSlotGuestLimit(event: Event, slotId?: string): number | undefined {
  if (slotId && event.slotGuestLimits && event.slotGuestLimits[slotId] != null) {
    const n = Number(event.slotGuestLimits[slotId]);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  if (event.maxGuestsPerSlot != null) {
    const n = Number(event.maxGuestsPerSlot);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return undefined;
}

export function getEffectiveCapacitySource(event: Event): 'slot' | 'tables' | 'both' {
  if (event.capacitySource) return event.capacitySource;
  const lim = getEffectiveSlotGuestLimit(event, undefined);
  const perSlot =
    event.slotGuestLimits &&
    Object.values(event.slotGuestLimits).some((v) => v != null && Number(v) >= 0);
  return lim != null || perSlot ? 'both' : 'tables';
}

/** Remaining guest “seats” for the slot limit, or null if unlimited. */
export function getRemainingSlotPartyCapacity(
  event: Event,
  reservations: Reservation[],
  reservationDate: string,
  reservationTime: string,
  slotId: string | undefined,
  excludeReservationId?: number
): number | null {
  const limit = getEffectiveSlotGuestLimit(event, slotId);
  if (limit == null) return null;
  const { partyBooked } = aggregateEventSlotGuests(
    reservations,
    event.id,
    reservationDate,
    reservationTime,
    { slotId, excludeReservationId }
  );
  return Math.max(0, limit - partyBooked);
}

export function buffetPartyFitsSlot(
  event: Event,
  reservations: Reservation[],
  reservationDate: string,
  reservationTime: string,
  slotId: string | undefined,
  partySize: number,
  excludeReservationId?: number
): boolean {
  const rem = getRemainingSlotPartyCapacity(
    event,
    reservations,
    reservationDate,
    reservationTime,
    slotId,
    excludeReservationId
  );
  if (rem === null) return true;
  return rem >= partySize;
}

/**
 * Check if a table is taken for the given date/time by any reservation.
 * A table is taken if any reservation has the same date and time and either
 * tableId matches or assignedTableIds contains the table id.
 * Optional `bufferMinutes` also treats nearby times on the same date as taken.
 */
function isTableTakenForSlot(
  tableId: number,
  reservationDate: string,
  reservationTime: string,
  reservations: Reservation[],
  excludeReservationId?: number,
  bufferMinutes: number = 0,
): boolean {
  return reservations.some((r) => {
    if (r.id === excludeReservationId) return false;
    // Cancelled / completed / waitlist must not block tables for the same slot.
    if (!TABLE_HOLD_STATUSES.includes(r.status)) return false;
    if (r.reservationDate !== reservationDate) return false;
    if (bufferMinutes > 0) {
      if (!reservationTimesWithinMinutes(r.reservationTime, reservationTime, bufferMinutes)) {
        return false;
      }
    } else if (r.reservationTime !== reservationTime) {
      return false;
    }
    return reservationHoldsTable(r, tableId);
  });
}

/** Minutes from midnight for `HH:MM` / `H:MM`. */
export function reservationTimeToMinutes(time: string): number | null {
  const [h, m] = String(time || '').split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

/** True when two clock times on the same day are within `windowMinutes` (inclusive). */
export function reservationTimesWithinMinutes(
  aTime: string,
  bTime: string,
  windowMinutes: number,
): boolean {
  const a = reservationTimeToMinutes(aTime);
  const b = reservationTimeToMinutes(bTime);
  if (a == null || b == null) return aTime === bTime;
  return Math.abs(a - b) <= windowMinutes;
}

/** Default turnover buffer: don't suggest the same tables within an hour of another booking. */
export const RESERVATION_TABLE_BUFFER_MINUTES = 60;

/**
 * Active reservations that hold any of `tableIds` within ±buffer of the slot.
 */
export function getConflictingReservationsForTables(
  reservations: Reservation[],
  reservationDate: string,
  reservationTime: string,
  tableIds: number[],
  options?: { bufferMinutes?: number; excludeReservationId?: number },
): Reservation[] {
  const buffer = options?.bufferMinutes ?? RESERVATION_TABLE_BUFFER_MINUTES;
  const ids = new Set(tableIds.map(Number));
  if (ids.size === 0) return [];
  return reservations.filter((r) => {
    if (options?.excludeReservationId != null && r.id === options.excludeReservationId) return false;
    if (!TABLE_HOLD_STATUSES.includes(r.status)) return false;
    if (r.reservationDate !== reservationDate) return false;
    if (!reservationTimesWithinMinutes(r.reservationTime, reservationTime, buffer)) return false;
    const held = [r.tableId, ...(r.assignedTableIds ?? [])].filter((id) => id != null).map(Number);
    return held.some((id) => ids.has(id));
  });
}

/**
 * Tables that are free (not reserved) for the given date and time.
 * Pass `bufferMinutes` (e.g. 60) to also hide tables booked nearby on the same day.
 */
export function getTablesFreeForSlot(
  tables: Table[],
  reservations: Reservation[],
  reservationDate: string,
  reservationTime: string,
  excludeReservationId?: number,
  bufferMinutes: number = 0,
): Table[] {
  return tables.filter(
    (t) =>
      !isTableTakenForSlot(
        t.id,
        reservationDate,
        reservationTime,
        reservations,
        excludeReservationId,
        bufferMinutes,
      )
  );
}

/**
 * Total available seating capacity for the given date/time.
 * Optionally capped by event maxCapacity.
 */
export function getTotalAvailableCapacity(
  tables: Table[],
  reservations: Reservation[],
  reservationDate: string,
  reservationTime: string,
  eventMaxCapacity?: number,
  excludeReservationId?: number
): number {
  const free = getTablesFreeForSlot(
    tables,
    reservations,
    reservationDate,
    reservationTime,
    excludeReservationId
  );
  const sum = free.reduce((acc, t) => acc + t.capacity, 0);
  if (eventMaxCapacity != null && eventMaxCapacity >= 0) {
    return Math.min(sum, eventMaxCapacity);
  }
  return sum;
}

/**
 * Tables that are free for the slot and have capacity >= partySize.
 */
export function getTablesFittingParty(
  tables: Table[],
  reservations: Reservation[],
  reservationDate: string,
  reservationTime: string,
  partySize: number,
  excludeReservationId?: number,
  bufferMinutes: number = 0,
): Table[] {
  const free = getTablesFreeForSlot(
    tables,
    reservations,
    reservationDate,
    reservationTime,
    excludeReservationId,
    bufferMinutes,
  );
  return free.filter((t) => t.capacity >= partySize);
}

/**
 * Max single-table capacity among all tables (for deciding if party fits one table).
 */
export function getMaxSingleTableCapacity(tables: Table[]): number {
  if (tables.length === 0) return 0;
  return Math.max(...tables.map((t) => t.capacity));
}

/**
 * Whether the selected table has enough capacity for the party.
 */
export function tableFitsParty(table: Table | null, partySize: number): boolean {
  if (!table) return false;
  return table.capacity >= partySize;
}

export type PartyTableAssignmentMode = 'single' | 'join' | 'impossible';

export interface PartyTableAssignmentSuggestion {
  mode: PartyTableAssignmentMode;
  tables: Table[];
  capacity: number;
  /** False when the join set spans tables that are farther than the adjacency threshold. */
  adjacent: boolean;
}

/** Floor % distance — seed layout tables are often ~15–25 apart; keep joins in one pocket. */
export const TABLE_JOIN_ADJACENCY_PCT = 22;

export function tableFloorDistance(a: Table, b: Table): number {
  const ax = typeof a.x === 'number' && Number.isFinite(a.x) ? a.x : 50;
  const ay = typeof a.y === 'number' && Number.isFinite(a.y) ? a.y : 50;
  const bx = typeof b.x === 'number' && Number.isFinite(b.x) ? b.x : 50;
  const by = typeof b.y === 'number' && Number.isFinite(b.y) ? b.y : 50;
  return Math.hypot(ax - bx, ay - by);
}

function clusterDiameter(tables: Table[]): number {
  let max = 0;
  for (let i = 0; i < tables.length; i++) {
    for (let j = i + 1; j < tables.length; j++) {
      max = Math.max(max, tableFloorDistance(tables[i], tables[j]));
    }
  }
  return max;
}

function minDistanceToCluster(candidate: Table, cluster: Table[]): number {
  if (cluster.length === 0) return 0;
  return Math.min(...cluster.map((t) => tableFloorDistance(candidate, t)));
}

/** True when tables form one connected pocket (each next table within adjacency of the cluster). */
export function isAdjacentTableCluster(
  tables: Table[],
  maxAdjacentDistance: number = TABLE_JOIN_ADJACENCY_PCT,
): boolean {
  if (tables.length <= 1) return true;
  const remaining = [...tables];
  const cluster: Table[] = [remaining.shift()!];
  while (remaining.length > 0) {
    let bestIdx = -1;
    let bestDist = Infinity;
    for (let i = 0; i < remaining.length; i++) {
      const d = minDistanceToCluster(remaining[i], cluster);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
    if (bestIdx < 0 || bestDist > maxAdjacentDistance + 0.001) return false;
    cluster.push(remaining.splice(bestIdx, 1)[0]);
  }
  return true;
}

/**
 * Grow a contiguous cluster from `seed` by repeatedly adding the nearest free table
 * within `maxAdjacentDistance` until capacity covers the party.
 */
function growAdjacentCluster(
  seed: Table,
  freeTables: Table[],
  need: number,
  maxAdjacentDistance: number,
): Table[] | null {
  const picked: Table[] = [seed];
  let sum = seed.capacity;
  const remaining = freeTables.filter((t) => Number(t.id) !== Number(seed.id));

  while (sum < need && remaining.length > 0) {
    let bestIdx = -1;
    let bestDist = Infinity;
    for (let i = 0; i < remaining.length; i++) {
      const d = minDistanceToCluster(remaining[i], picked);
      if (d <= maxAdjacentDistance + 0.001 && d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
    if (bestIdx < 0) break;
    const next = remaining.splice(bestIdx, 1)[0];
    picked.push(next);
    sum += next.capacity;
  }

  return sum >= need ? picked : null;
}

/** Grow from an already-picked seed set (e.g. staff partial selection). */
function growFromSeedTables(
  seeds: Table[],
  freeTables: Table[],
  need: number,
  maxAdjacentDistance: number,
): Table[] | null {
  if (seeds.length === 0) return null;
  const seedIds = new Set(seeds.map((t) => Number(t.id)));
  const picked: Table[] = [...seeds];
  let sum = seeds.reduce((s, t) => s + t.capacity, 0);
  if (sum >= need) return picked;
  const remaining = freeTables.filter((t) => !seedIds.has(Number(t.id)));

  while (sum < need && remaining.length > 0) {
    let bestIdx = -1;
    let bestDist = Infinity;
    for (let i = 0; i < remaining.length; i++) {
      const d = minDistanceToCluster(remaining[i], picked);
      if (d <= maxAdjacentDistance + 0.001 && d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
    if (bestIdx < 0) break;
    const next = remaining.splice(bestIdx, 1)[0];
    picked.push(next);
    sum += next.capacity;
  }

  return sum >= need ? picked : null;
}

function clusterKey(tables: Table[]): string {
  return [...tables.map((t) => Number(t.id))].sort((a, b) => a - b).join(',');
}

function rankJoinClusters(a: Table[], b: Table[]): number {
  if (a.length !== b.length) return a.length - b.length;
  const da = clusterDiameter(a);
  const db = clusterDiameter(b);
  if (da !== db) return da - db;
  const ca = a.reduce((s, t) => s + t.capacity, 0);
  const cb = b.reduce((s, t) => s + t.capacity, 0);
  return ca - cb || Number(a[0].id) - Number(b[0].id);
}

function collectAdjacentJoinClusters(
  freeTables: Table[],
  need: number,
  maxAdjacentDistance: number,
): Table[][] {
  const byKey = new Map<string, Table[]>();
  for (const seed of freeTables) {
    const cluster = growAdjacentCluster(seed, freeTables, need, maxAdjacentDistance);
    if (!cluster) continue;
    const key = clusterKey(cluster);
    if (!byKey.has(key)) byKey.set(key, cluster);
  }
  return [...byKey.values()].sort(rankJoinClusters);
}

export interface JoinCombinationSuggestion {
  tables: Table[];
  adjacent: boolean;
  capacity: number;
}

/**
 * Complete a partial join selection with nearest free neighbors until capacity covers the party.
 * Returns null when the partial set cannot be completed within adjacency (tries wider once).
 */
export function completeJoinFromPartial(
  selected: Table[],
  freeTables: Table[],
  partySize: number,
  options?: { maxAdjacentDistance?: number },
): JoinCombinationSuggestion | null {
  const need = Math.max(1, Math.floor(partySize) || 1);
  if (selected.length === 0) return null;
  const selectedCap = selected.reduce((s, t) => s + t.capacity, 0);
  if (selectedCap >= need) {
    return {
      tables: selected,
      adjacent: isAdjacentTableCluster(selected, options?.maxAdjacentDistance),
      capacity: selectedCap,
    };
  }

  const maxAdj = options?.maxAdjacentDistance ?? TABLE_JOIN_ADJACENCY_PCT;
  const tight = growFromSeedTables(selected, freeTables, need, maxAdj);
  if (tight) {
    return {
      tables: tight,
      adjacent: true,
      capacity: tight.reduce((s, t) => s + t.capacity, 0),
    };
  }
  const wider = growFromSeedTables(selected, freeTables, need, maxAdj * 1.5);
  if (wider) {
    return {
      tables: wider,
      adjacent: false,
      capacity: wider.reduce((s, t) => s + t.capacity, 0),
    };
  }
  return null;
}

/**
 * Ranked nearby join combinations for a party.
 * - `excludeTableIds`: omit these tables (e.g. ones staff just cleared) so other pockets appear.
 * - `mustIncludeTableIds`: put a completion of the current partial selection first.
 */
export function suggestJoinCombinations(
  freeTables: Table[],
  partySize: number,
  options?: {
    maxAdjacentDistance?: number;
    excludeTableIds?: number[];
    mustIncludeTableIds?: number[];
    limit?: number;
  },
): JoinCombinationSuggestion[] {
  const need = Math.max(1, Math.floor(partySize) || 1);
  const limit = Math.max(1, options?.limit ?? 4);
  const exclude = new Set((options?.excludeTableIds ?? []).map(Number));
  const mustIds = (options?.mustIncludeTableIds ?? []).map(Number);
  const pool = freeTables.filter((t) => !exclude.has(Number(t.id)));
  if (pool.length === 0) return [];

  const maxAdj = options?.maxAdjacentDistance ?? TABLE_JOIN_ADJACENCY_PCT;
  const out: JoinCombinationSuggestion[] = [];
  const seen = new Set<string>();

  const push = (tables: Table[], adjacent: boolean) => {
    if (tables.length === 0) return;
    const key = clusterKey(tables);
    if (seen.has(key)) return;
    seen.add(key);
    out.push({
      tables,
      adjacent,
      capacity: tables.reduce((s, t) => s + t.capacity, 0),
    });
  };

  if (mustIds.length > 0) {
    const seeds = mustIds
      .map((id) => freeTables.find((t) => Number(t.id) === id))
      .filter(Boolean) as Table[];
    // Completion always uses the full free set — exclusions only shape *alternate* pockets.
    if (seeds.length === mustIds.length) {
      const completed = completeJoinFromPartial(seeds, freeTables, need, { maxAdjacentDistance: maxAdj });
      if (completed) push(completed.tables, completed.adjacent);
    }
  }

  for (const cluster of collectAdjacentJoinClusters(pool, need, maxAdj)) {
    push(cluster, true);
    if (out.length >= limit) return out.slice(0, limit);
  }

  for (const cluster of collectAdjacentJoinClusters(pool, need, maxAdj * 1.5)) {
    push(cluster, false);
    if (out.length >= limit) return out.slice(0, limit);
  }

  if (out.length === 0) {
    const { tables, adjacent } = suggestTablesToJoinDetailed(pool, need, {
      maxAdjacentDistance: maxAdj,
    });
    if (tables.length > 0) push(tables, adjacent);
  }

  return out.slice(0, limit);
}

/**
 * Suggest free tables for a party size.
 * - Prefer a single smallest table that fits.
 * - Otherwise grow floor-adjacent clusters (by x/y) so suggested joins are not across the room.
 * - Falls back to a slightly wider adjacency, then to capacity-only greedy (adjacent=false).
 */
export function suggestTablesToJoin(
  freeTables: Table[],
  partySize: number,
  options?: { maxAdjacentDistance?: number },
): Table[] {
  const result = suggestTablesToJoinDetailed(freeTables, partySize, options);
  return result.tables;
}

export function suggestTablesToJoinDetailed(
  freeTables: Table[],
  partySize: number,
  options?: { maxAdjacentDistance?: number },
): { tables: Table[]; adjacent: boolean } {
  const need = Math.max(1, Math.floor(partySize) || 1);
  if (freeTables.length === 0) return { tables: [], adjacent: true };

  const fitting = freeTables
    .filter((t) => t.capacity >= need)
    .sort((a, b) => a.capacity - b.capacity || Number(a.id) - Number(b.id));
  if (fitting.length > 0) return { tables: [fitting[0]], adjacent: true };

  const maxAdj = options?.maxAdjacentDistance ?? TABLE_JOIN_ADJACENCY_PCT;
  const clusters = collectAdjacentJoinClusters(freeTables, need, maxAdj);
  if (clusters.length > 0) return { tables: clusters[0], adjacent: true };

  const wider = collectAdjacentJoinClusters(freeTables, need, maxAdj * 1.5);
  if (wider.length > 0) return { tables: wider[0], adjacent: false };

  // Last resort: capacity-greedy (may span distant tables — UI should warn).
  const byLargest = [...freeTables].sort(
    (a, b) => b.capacity - a.capacity || Number(a.id) - Number(b.id),
  );
  const picked: Table[] = [];
  let sum = 0;
  for (const t of byLargest) {
    picked.push(t);
    sum += t.capacity;
    if (sum >= need) {
      return { tables: picked, adjacent: false };
    }
  }
  return { tables: [], adjacent: true };
}

/**
 * Classify how to seat a party given free tables and venue max single capacity.
 */
export function evaluatePartyTableAssignment(
  freeTables: Table[],
  partySize: number,
  maxSingleCapacity: number,
  options?: { maxAdjacentDistance?: number },
): PartyTableAssignmentSuggestion {
  const need = Math.max(1, Math.floor(partySize) || 1);
  const totalFree = freeTables.reduce((s, t) => s + t.capacity, 0);
  if (totalFree < need) {
    return { mode: 'impossible', tables: [], capacity: totalFree, adjacent: true };
  }

  if (need <= maxSingleCapacity) {
    const single = freeTables
      .filter((t) => t.capacity >= need)
      .sort((a, b) => a.capacity - b.capacity || Number(a.id) - Number(b.id));
    if (single.length > 0) {
      return { mode: 'single', tables: [single[0]], capacity: single[0].capacity, adjacent: true };
    }
  }

  const { tables: joined, adjacent } = suggestTablesToJoinDetailed(freeTables, need, options);
  if (joined.length === 0) {
    return { mode: 'impossible', tables: [], capacity: totalFree, adjacent: true };
  }
  const capacity = joined.reduce((s, t) => s + t.capacity, 0);
  if (joined.length === 1) {
    return { mode: 'single', tables: joined, capacity, adjacent: true };
  }
  return { mode: 'join', tables: joined, capacity, adjacent };
}

export interface BuffetCapacityCheck {
  mustWaitlist: boolean;
  requiresMerge: boolean;
  slotOk: boolean;
  tablesOk: boolean;
  remainingSlotGuests: number | null;
}

/**
 * Combined slot limit + table capacity for public buffet booking.
 * `capacitySource`: slot = only slot limit; tables = only venue tables; both = require both.
 */
export function evaluateBuffetBookingCapacity(
  event: Event,
  tables: Table[],
  reservations: Reservation[],
  reservationDate: string,
  reservationTime: string,
  slotId: string | undefined,
  partySize: number,
  excludeReservationId?: number
): BuffetCapacityCheck {
  const maxSingle = getMaxSingleTableCapacity(tables);
  const totalCap = getTotalAvailableCapacity(
    tables,
    reservations,
    reservationDate,
    reservationTime,
    event.maxCapacity,
    excludeReservationId
  );
  const src = getEffectiveCapacitySource(event);
  const rem = getRemainingSlotPartyCapacity(
    event,
    reservations,
    reservationDate,
    reservationTime,
    slotId,
    excludeReservationId
  );
  const slotOk = rem === null || rem >= partySize;
  const tablesOk = totalCap >= partySize;
  let mustWaitlist = false;
  if (src === 'slot') mustWaitlist = !slotOk;
  else if (src === 'tables') mustWaitlist = !tablesOk;
  else mustWaitlist = !slotOk || !tablesOk;
  const canSeatByTables = tablesOk && !mustWaitlist;
  const requiresMerge = partySize > maxSingle && canSeatByTables;
  return {
    mustWaitlist,
    requiresMerge,
    slotOk,
    tablesOk,
    remainingSlotGuests: rem,
  };
}
